/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import type { Instruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import type { NavigationView } from '@jira/polaris__component-navigation-store/src/NavigationView.types.tsx';
import type { ItemId, TreeItem, TreeObject } from '@jira/polaris__lib-tree/src/ItemId.types.tsx';

export type ItemType = 'view' | 'section' | 'placeholder';

export type TreeItemData = {
	title?: string;
	type?: ItemType;
	view?: SimplifiedView;
	parentId?: ItemId;
};

export type SimplifiedView = {
	id: string;
	polarisId: number;
	localId: LocalViewId;
	views?: SimplifiedView[];
};

export interface JpdTreeItem extends TreeItem {
	data: TreeItemData;
}

export type SimplifiedSectionTreeNode = SimplifiedView & {
	collapsed: boolean;
};

export type JpdTreeObject = TreeObject<JpdTreeItem>;

class TreeBuilder {
	rootId: ItemId;

	items: Record<ItemId, TreeItem> = {};

	constructor(rootId: ItemId, data: TreeItemData, isExpanded?: boolean) {
		const rootItem = this.createItem(rootId, data, [], isExpanded);
		this.rootId = rootItem.id;
		this.items[rootItem.id] = rootItem;
	}

	// go/jfe-eslint
	withLeaf(
		id: ItemId,
		data: TreeItemData,
		blockedInstructions?: Instruction['type'][],
	): TreeBuilder {
		const leafItem = this.createItem(id, data, blockedInstructions);
		this.addItemToRoot(leafItem.id);
		this.items[leafItem.id] = leafItem;
		return this;
	}

	// go/jfe-eslint
	withSubTree(tree: TreeBuilder): TreeBuilder {
		const subTree = tree.build();
		this.addItemToRoot(subTree.rootId);
		this.items = { ...this.items, ...subTree.items };
		return this;
	}

	addItemToRoot(id: ItemId) {
		const rootItem = this.items[this.rootId];
		rootItem.children.push(id);
		rootItem.hasChildren = true;
	}

	createItem = (
		id: ItemId,
		data: TreeItemData,
		blockedInstructions?: Instruction['type'][],
		isExpanded = false,
	): TreeItem => ({
		id,
		data,
		isExpanded,
		children: [],
		hasChildren: false,
		blockedInstructions,
	});

	// Builds the tree object
	build(): JpdTreeObject {
		return {
			rootId: this.rootId,
			items: this.items,
		};
	}
}

export const createTree = (
	topLevelViews: NavigationView[],
): TreeObject<JpdTreeItem> | undefined => {
	const viewsTree = new TreeBuilder('root', {});

	const createSectionNode = ({ localId, views, isExpanded }: NavigationView) => {
		const subTree = new TreeBuilder(
			localId,
			{
				type: 'section',
			},
			isExpanded,
		);
		views?.forEach((view: NavigationView) => {
			subTree.withLeaf(
				view.localId,
				{
					type: 'view',
				},
				['make-child'],
			);
		});
		subTree.withLeaf(
			`${localId}-placeholder`,
			{
				type: 'placeholder',
				parentId: localId,
			},
			['reorder-below', 'make-child'],
		);
		viewsTree.withSubTree(subTree);
	};

	const createViewNode = (view: NavigationView) => {
		viewsTree.withLeaf(view.localId, { type: 'view', view }, ['make-child']);
	};

	// Sort views and viewsets
	const treeNodes: Array<{
		type: string;
		data: { view: NavigationView | null };
	}> = [];

	topLevelViews.forEach((view: NavigationView) => {
		treeNodes.push({
			type: view.viewType === 'SECTION' ? 'section' : 'view',
			data: { view },
		});
	});

	// Generate all tree nodes
	treeNodes.forEach(({ type, data }) => {
		if (type === 'section' && data.view) {
			createSectionNode(data.view);
		}
		if (type === 'view' && data.view) {
			createViewNode(data.view);
		}
	});

	return viewsTree.build();
};
